import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, Fade, Slide, IconButton, alpha } from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useHistory, useLocation, useParams } from "react-router-dom";
import useApp from "../../store/contexts/AppContext";
import { REACT_APP_BEFORE_LOGIN_REDIRECT_URL, CHAT_ROOMS } from "../../config";
import WaitForPageLoad from "../../components/WaitForPageLoad";
import Header from "../../components/Header";
import ChatLists from "../../components/Chats/ChatLists";
import SearchResults from "../../components/Chats/ChatLists/SearchResults";
import ActiveChat from "../../components/Chats/ActiveChat";
import EmptyChatDashboard from "../../components/Chats/ActiveChat/EmptyChatDashboard";

// Main container
const PageContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.background.default
    : theme.palette.background.paper,
}));

// Chat Container
const ChatContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "350px 1fr",
  // flex: 1 + minHeight: 0 fills whatever the header leaves — works whether
  // the header is full height, collapsed, or anything in between.
  flex: 1,
  minHeight: 0,
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.background.default
    : theme.palette.background.paper,
  position: "relative",
  overflow: "hidden",

  "@media (max-width: 900px)": {
    gridTemplateColumns: "300px 1fr",
  },

  "@media (max-width: 600px)": {
    display: "flex",
    gridTemplateColumns: "1fr",
  },
}));

// Left Sidebar - WhatsApp style clean panel
const LeftSidebar = styled(Box)(({ theme, hasActiveChat }) => ({
  width: "100%",
  height: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: theme.palette.mode === "dark" ? "#111b21" : "#ffffff",
  borderRight: `1px solid ${theme.palette.mode === "dark"
    ? alpha("#fff", 0.06)
    : alpha("#000", 0.1)}`,
  color: theme.palette.mode === "dark" ? "#fff" : "#1a1a1a",

  "@media (max-width: 600px)": {
    display: hasActiveChat ? "none" : "flex",
  },
}));

// WhatsApp-style search container
const SearchContainer = styled("div")(({ theme }) => ({
  padding: "8px 12px",
  flexShrink: 0,
  zIndex: 10,
  background: "transparent",
}));

const SearchInputWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  display: "flex",
  alignItems: "center",
});

const SearchIconWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.5) : alpha("#000", 0.45),
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  zIndex: 1,

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

const SearchInput = styled("input")(({ theme, hasValue }) => ({
  width: "100%",
  padding: hasValue ? "10px 40px 10px 44px" : "10px 16px 10px 44px",
  borderRadius: "24px",
  border: "none",
  background: theme.palette.mode === "dark"
    ? alpha("#fff", 0.08)
    : alpha("#000", 0.07),
  color: theme.palette.mode === "dark" ? "#fff" : "#1a1a1a",
  fontSize: "15px",
  fontWeight: 400,
  outline: "none",
  transition: "background 0.2s ease",
  fontFamily: "inherit",

  "&::placeholder": {
    color: theme.palette.mode === "dark" ? alpha("#fff", 0.4) : alpha("#000", 0.4),
  },

  "&:focus": {
    background: theme.palette.mode === "dark"
      ? alpha("#fff", 0.12)
      : alpha("#000", 0.09),
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  padding: "4px",
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.5) : alpha("#000", 0.4),
  zIndex: 2,
  background: theme.palette.mode === "dark"
    ? alpha("#fff", 0.12)
    : alpha("#000", 0.1),
  borderRadius: "50%",
  width: "24px",
  height: "24px",

  "&:hover": {
    background: theme.palette.mode === "dark"
      ? alpha("#fff", 0.2)
      : alpha("#000", 0.15),
  },

  "& .MuiSvgIcon-root": {
    fontSize: "14px",
  },
}));

const ChatListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  height: "100%",
  minHeight: 0,

  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(0,0,0,0.12)",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.24)"
      : "rgba(0,0,0,0.20)",
  },
}));

const CenterChat = styled(Box)(({ theme, hasActiveChat }) => ({
  position: "relative",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.palette.mode === "dark"
    ? theme.palette.background.default
    : theme.palette.background.paper,

  "@media (max-width: 600px)": {
    display: hasActiveChat ? "flex" : "none",
    width: "100%",
  },
}));

export default function Messages() {
  const {
    appState: { accessToken, dataLoading, activeChat, userData, filteredChats, searchInProgress, chats },
    searchThroughChats,
    switchActiveChat,
    fetchAUserByIdApiCall,
  } = useApp();

  const history = useHistory();
  const location = useLocation();
  const { username: usernameParam } = useParams();
  const [searchValue, setSearchValue] = useState("");
  const [mounted, setMounted] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);

  // Refs so initiateChatFromUrl always reads current values without them being deps
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const activeChatIdRef = useRef(activeChat?.id);
  activeChatIdRef.current = activeChat?.id;

  // Only changes when chat IDs themselves change (not when unread_count etc. changes).
  // SET_ACTIVE_CHAT always creates a new chats array reference (via .map for unread reset),
  // which would otherwise re-trigger initiateChatFromUrl and revert manual selections.
  const chatIds = useMemo(() => chats.map((c) => c?.id).join(","), [chats]);

  // A chat is only "active" if it has a real id (existing chat) or valid participants (new chat setup).
  // Guards against stale/partial activeChat state showing a generic "User" header.
  const hasActiveChat = useMemo(
    () => Object.keys(activeChat).length > 0 && !!(activeChat.id || activeChat.room_participants?.length),
    [activeChat]
  );

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setShowSearchResults(!!value.trim());

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim()) {
      // Debounce: wait 350ms after the last keystroke before hitting the backend
      searchTimeoutRef.current = setTimeout(() => {
        searchThroughChats(value.trim());
      }, 350);
    } else {
      searchThroughChats("");
      setShowSearchResults(false);
    }
  };

  const handleClearSearch = () => {
    setSearchValue("");
    searchThroughChats("");
    setShowSearchResults(false);
  };

  const handleSelectChat = (chat) => {
    // Prefer the full chat object from state.chats so we get messages + pagination.
    // The search result object (from filteredChats) may only have a subset of that data.
    const fullChat = chats.find((c) => c.id?.toString() === chat.id?.toString()) || chat;
    switchActiveChat(fullChat);
    handleClearSearch();

    if (fullChat.type === CHAT_ROOMS.TYPES.SINGLE) {
      const otherParticipant = fullChat.room_participants?.find(
        (p) => p.id?.toString() !== userData.id?.toString()
      );
      history.push(otherParticipant?.username ? `/messages/${otherParticipant.username}` : "/messages");
    } else {
      history.push("/messages");
    }
  };

  const handleSelectNewUser = async (user) => {
    // Create a temporary chat object for a new conversation
    const newChat = {
      id: null,
      name: user.username,
      type: CHAT_ROOMS.TYPES.SINGLE,
      has_connected_before: false,
      room_participants: [
        {
          id: userData.id,
          username: userData.username,
          is_admin: 1,
          is_online: userData.is_online,
          last_seen: userData.last_seen,
        },
        {
          id: user.id,
          username: user.username,
          is_admin: 0,
          is_online: user.is_online,
          last_seen: user.last_seen,
        },
      ],
      messages: [],
      pagination: { current_page: 1, next_page: null, page_size: 30, total_count: 0 },
      unread_count: 0,
    };

    // Push /messages (no username) so initiateChatFromUrl exits early and doesn't
    // try to re-fetch/re-open a chat for whatever username was previously in the URL.
    history.push("/messages");
    handleClearSearch();
    switchActiveChat(newChat);
  };

  useEffect(() => {
    if (!accessToken) {
      history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
    }
  }, [accessToken, history]);

  // Handle URL parameter for direct chat initiation.
  // Uses chatIds (not chats) as dep so the effect does NOT re-fire when SET_ACTIVE_CHAT
  // resets unread_count — that reducer always creates a new chats array reference even
  // though the IDs haven't changed, which would otherwise revert manual selections.
  // chatsRef / activeChatIdRef give the effect always-current values without extra deps.
  useEffect(() => {
    if (!usernameParam || !userData?.id) return;

    const initiateChatFromUrl = async () => {
      try {
        const currentChats = chatsRef.current;

        const existingChat = currentChats.find((chat) => {
          if (chat.type !== CHAT_ROOMS.TYPES.SINGLE) return false;
          const otherParticipant = chat.room_participants?.find(
            (p) => p.id?.toString() !== userData.id?.toString()
          );
          return otherParticipant?.username?.toLowerCase() === usernameParam.toLowerCase();
        });

        if (existingChat) {
          if (activeChatIdRef.current?.toString() !== existingChat.id?.toString()) {
            switchActiveChat(existingChat);
          }
        } else if (!isLoadingChat && currentChats.length > 0) {
          setIsLoadingChat(true);

          const response = await fetchAUserByIdApiCall(usernameParam);
          if (response.success && response.data) {
            const targetUser = response.data;

            const newChat = {
              id: null,
              name: targetUser.username,
              type: CHAT_ROOMS.TYPES.SINGLE,
              room_participants: [
                {
                  id: userData.id,
                  username: userData.username,
                  is_admin: 1,
                  is_online: userData.is_online,
                  last_seen: userData.last_seen,
                },
                {
                  id: targetUser.id,
                  username: targetUser.username,
                  is_admin: 0,
                  is_online: targetUser.is_online,
                  last_seen: targetUser.last_seen,
                },
              ],
              messages: [],
              pagination: { current_page: 1, next_page: null, page_size: 30, total_count: 0 },
              unread_count: 0,
              has_connected_before: false,
            };

            switchActiveChat(newChat);
          }
          setIsLoadingChat(false);
        }
      } catch (error) {
        console.error("Error fetching user for chat:", error);
        setIsLoadingChat(false);
      }
    };

    initiateChatFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameParam, userData?.id, chatIds]);

  useEffect(() => {
    setMounted(true);

    // Cleanup search timeout on unmount
    return () => {
      const timeout = searchTimeoutRef.current;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  if (dataLoading) return <WaitForPageLoad />;

  return (
    <PageContainer>
      <Header />
      <ChatContainer>
        {/* Left Sidebar - Chat List */}
        <Slide direction="right" in={mounted} timeout={400}>
          <LeftSidebar hasActiveChat={hasActiveChat}>
            <SearchContainer>
              <SearchInputWrapper>
                <SearchIconWrapper className={searchValue ? "active" : ""}>
                  <SearchIcon />
                </SearchIconWrapper>
                <SearchInput
                  placeholder="Search messages or people..."
                  onChange={(e) => handleSearchChange(e.target.value)}
                  value={searchValue}
                  hasValue={!!searchValue}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                {searchValue && (
                  <Fade in={true} timeout={200}>
                    <ClearButton
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                      size="small"
                    >
                      <CloseIcon />
                    </ClearButton>
                  </Fade>
                )}
              </SearchInputWrapper>
            </SearchContainer>

            <ChatListContainer>
              {showSearchResults ? (
                <SearchResults
                  searchResults={filteredChats}
                  searchInProgress={searchInProgress}
                  onSelectChat={handleSelectChat}
                  onSelectNewUser={handleSelectNewUser}
                  currentUserId={userData?.id}
                  activeChatId={activeChat?.id}
                />
              ) : (
                <ChatLists />
              )}
            </ChatListContainer>
          </LeftSidebar>
        </Slide>

        {/* Center - Active Chat */}
        <Fade in={mounted} timeout={600}>
          <CenterChat hasActiveChat={hasActiveChat || (isLoadingChat && !!usernameParam)}>
            {hasActiveChat ? (
              <ActiveChat />
            ) : isLoadingChat && usernameParam ? null : (
              <EmptyChatDashboard />
            )}
          </CenterChat>
        </Fade>
      </ChatContainer>

    </PageContainer>
  );
}
